import type { GlobalSearchSuggestion } from './types.ts';
import { useScrollLock } from '@base-ui/utils/useScrollLock';
import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useEffectEvent } from 'react';
import { PiMagnifyingGlass, PiXBold } from 'react-icons/pi';
import { useIntl } from 'react-intl';
import { SharedConfig } from '@_/config/shared-config.ts';
import { Autocomplete, AutocompleteInput } from '@_/ui';
import { placeholderI18n } from '#src/i18nMessages/index.ts';
import { GlobalSearchOptions } from './GlobalSearchOptions.tsx';
import { useGlobalSearch } from './useGlobalSearch.ts';

export function FullScreenGlobalSearch({ onClose }: { onClose?: () => void }) {
  const intl = useIntl();
  const routerStatus = useRouterState({ select: state => state.status });

  useScrollLock(true);

  const close = useEffectEvent(() => onClose?.());
  useEffect(() => {
    if (routerStatus === 'pending') {
      close();
    }
  }, [routerStatus]);

  const { autocompleteProps, onSubmit } = useGlobalSearch();

  const handleSubmit = useCallback<typeof onSubmit>(
    event => {
      onSubmit(event);
      onClose?.();
    },
    [onClose, onSubmit]
  );

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <Autocomplete<GlobalSearchSuggestion> {...autocompleteProps} inline>
        <div className="flex h-full flex-col">
          <div className="grow-0 px-4 py-3">
            <AutocompleteInput
              render={renderProps => (
                <form
                  className="flex items-center gap-x-3 rounded-full bg-gray-100 py-0 pr-3 pl-4"
                  onSubmit={handleSubmit}
                >
                  <PiMagnifyingGlass className="size-4 text-gray-900" />

                  <input
                    {...renderProps}
                    autoFocus
                    className="grow resize-none appearance-none border-0 bg-transparent px-0 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:ring-0 focus:outline-hidden"
                    maxLength={SharedConfig.maxLength.searchTerm}
                    placeholder={intl.formatMessage(placeholderI18n.globalSearch)}
                    type="text"
                  />

                  <button
                    aria-label="close"
                    className="border-0 bg-transparent"
                    onClick={onClose}
                    type="button"
                  >
                    <PiXBold className="size-4 text-sm text-gray-600" />
                  </button>
                </form>
              )}
            />
          </div>

          <div className="grow overflow-auto px-4 [--gutter-x:--spacing(4)]">
            <GlobalSearchOptions items={autocompleteProps.items} onSelectItem={onClose} />
          </div>
        </div>
      </Autocomplete>
    </div>
  );
}
